import type { ComponentType, JSX } from 'react';

import {
  BitzBuggyLogo,
  LocalTeaLogo,
  NerdyBotLogo,
  SmartNewsLogo,
  SurfTimeLogo,
  TradingAgentLogo,
} from '../logos';
import { useCurrentApp, type AppThemeId } from '../hooks';

interface LogoProps {
  size?: number;
  className?: string;
  title?: string;
}

interface AppMeta {
  Logo: ComponentType<LogoProps>;
  name: string;
}

/**
 * Map from current-app theme id to the chip's logo + display name.
 * `nerdy-paradise` intentionally has no chip (we're ON the launcher; the
 * top-left Nerdy Paradise mark already covers it).
 */
const APP_META: Record<Exclude<AppThemeId, 'nerdy-paradise'>, AppMeta> = {
  'llm-chat': { Logo: NerdyBotLogo, name: 'NerdyBot' },
  'smart-news': { Logo: SmartNewsLogo, name: 'Smart News' },
  surftime: { Logo: SurfTimeLogo, name: 'SurfTime' },
  localtea: { Logo: LocalTeaLogo, name: 'LocalTea' },
  'trading-agent': { Logo: TradingAgentLogo, name: 'Trading Agent' },
  bitzbuggy: { Logo: BitzBuggyLogo, name: 'BitzBuggy' },
};

/**
 * Pill that announces which app the user is currently inside. Uses the
 * theme's accent-soft / accent tokens so each app gets its own colored chip
 * without any per-app CSS branching.
 *
 * Returns `null` on the launcher so the shell doesn't double-label itself.
 * Styled by `.shell__app` / `.shell__app-mark` in `tokens.css`.
 */
export function ShellAppChip(): JSX.Element | null {
  const appId = useCurrentApp();
  if (appId === 'nerdy-paradise') return null;

  const meta = APP_META[appId];
  const { Logo, name } = meta;

  return (
    // data-testid: the root has only an aria-label (no ARIA role is implied
    // by that alone), so it has no stable accessible query — this testid is
    // the shell's contract for identifying the chip in tests.
    <div className="shell__app" aria-label={`Currently in ${name}`} data-testid="shell-app-chip">
      <Logo size={18} className="shell__app-mark" title={name} />
      <span>{name}</span>
    </div>
  );
}
